import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as types from '../../Redux/AuthReducer/actionTypes';

// Single-login handoff from the landing site.
//
// The landing site signs the MainDoctor in server side (POST /mainDoctor/login)
// and sends the browser here as
//   /sso#auth=<encodeURIComponent(base64(JSON.stringify({ token, data })))>
// The fragment never reaches any server, and it is wiped from the address bar
// and history before anything else happens. The session is then stored exactly
// as the normal login form stores it (see Redux/AuthReducer/action.js).

const decodeAuth = (hash) => {
  const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  const encoded = params.get('auth');
  if (!encoded) return null;

  // URLSearchParams already undoes one level of percent-encoding; a second
  // decode is harmless for base64 and covers a double-encoded value.
  let b64 = encoded;
  try {
    b64 = decodeURIComponent(encoded);
  } catch (_) {}
  b64 = b64.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';

  // base64 -> bytes -> UTF-8 text, so non-ASCII names survive.
  const binary = window.atob(b64);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  const json = new TextDecoder('utf-8').decode(bytes);
  const parsed = JSON.parse(json);

  if (!parsed || typeof parsed.token !== 'string' || !parsed.token) return null;
  if (!parsed.data || typeof parsed.data !== 'object' || !parsed.data.role) return null;
  return { token: parsed.token, data: parsed.data };
};

export default function SsoLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash;
    // Remove the token from the URL before doing anything with it.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    let auth = null;
    try {
      auth = decodeAuth(hash);
    } catch (err) {
      auth = null;
    }

    if (!auth) {
      navigate('/login', { replace: true });
      return;
    }

    sessionStorage.setItem('clinic-application-jwt', JSON.stringify({ data: auth.data, token: auth.token }));
    dispatch({
      type: types.USER_LOGIN_SUCCESS,
      payload: { message: '', token: auth.token, data: auth.data },
    });
    navigate('/dashboard', { replace: true });
  }, [dispatch, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
      Signing you in…
    </div>
  );
}
